// @erp/module-audit/fe · the query-key factory (DEV-GUIDE-002 §3.5 — ad-hoc
// string keys in components are BANNED: targeted invalidation dies on a typo).
// Mirrors modules/cms/src/fe/keys.ts.
//
// Shape: hierarchical tuples rooted at ['audit'], one branch per read entry
// point (which is one branch per INDEX — see ADR-004 §7) —
//   ['audit']                                  → auditKeys.all
//   ['audit','trail','feed', f]                → the global feed
//   ['audit','trail','record', entity, id, f]  → the per-record history
//   ['audit','trail','detail', id]             → one row
//
// The API is ONE list route whose filters pick the index (ADR-004 §7); the
// record branch gets its own key anyway because it is the one a HOST screen
// invalidates by name after its own mutation — sharing the feed's key would make
// that invalidation sweep every unrelated console page too.
// Prefix-invalidation is the point: ['audit','trail'] sweeps every view.
//
// NOTHING here is ever invalidated by a mutation of this module's own — the
// trail is append-only and has no write surface. The keys exist so a HOST screen
// can refresh a record's history after ITS mutation lands (invalidate
// auditKeys.trail.record(entity, id)), which is the one legitimate invalidation.

/** The one root. Everything below composes from it. */
const ROOT = ["audit"] as const;

export const auditKeys = {
  all: ROOT,
  trail: {
    all: () => [...ROOT, "trail"] as const,
    feed: <F>(filters: F) => [...ROOT, "trail", "feed", filters] as const,
    /**
     * The console's stat row (GET /trail/stats, 2026-09-22). The WINDOW is part of
     * the key — the figures are a window, never a total, so two windows are two
     * different answers and must never share a cache entry.
     */
    stats: <F>(window: F) => [...ROOT, "trail", "stats", window] as const,
    /** The RecordHistory feed — the key a host screen invalidates after its own mutation. */
    record: <F>(entity: string, entityId: string, filters: F) =>
      [...ROOT, "trail", "record", entity, entityId, filters] as const,
    /** Prefix for "everything about this record", whatever the page filters are. */
    recordAll: (entity: string, entityId: string) => [...ROOT, "trail", "record", entity, entityId] as const,
    detail: (id: string) => [...ROOT, "trail", "detail", id] as const,
  },
} as const;

export type AuditKeys = typeof auditKeys;
